import { format } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";

import { Loader } from "fe-shared/src/components";
import React, { ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  adjustmentReasonOtherCode,
  DISPOSITION_EDIT_BUTTON_PORTAL_ID,
} from "../../consts";

import useAdjustmentReasons from "../../hooks/useAdjustmentReasons";

import { getActualTransactionDate } from "../../helpers/dates";

import {
  Retreader,
  Transaction,
  TransactionEventType,
  TransactionType,
} from "../../types";

import { LinkedTransactions } from "../index";

import LocationsMap from "../LocationsMap";
import styles from "./DetailsLocationsSection.module.scss";

export interface Props {
  transaction: Transaction | Retreader;
  sectionAction?: React.ReactElement;
  isStaffView?: boolean;
  children?: ReactNode;
  isInitiatedDelivery?: boolean;
  canOpenRequest?: boolean;
}

const DetailsLocationsSection = ({
  transaction,
  sectionAction,
  isStaffView = false,
  children,
  isInitiatedDelivery = false,
  canOpenRequest = false,
}: Props) => {
  const {
    syncedDate,
    linkedTransactions,
    events = [],
    transactionType = TransactionType.Delivery,
    transactionNumber,
    adjustmentReasons = [],
    adjustmentOtherReasonComment,
    submittedDate,
    completedDate,
    actualTransactionDate,
    targetWarehouse,
    regulatoryYear,
    submittedBy,
    requestNumber,
  } = transaction;
  const { t } = useTranslation();
  const showedEvents = [TransactionEventType.Initiated];

  const isAdjustment = transactionType === TransactionType.Adjustment;
  const isDisposition = transactionType === TransactionType.Disposition;
  const isRetreader = transactionType === TransactionType.Retreading;
  const shouldShowLocationMap = !(isAdjustment || isDisposition || isRetreader);
  const isLinkedTransactions =
    (!!linkedTransactions && !!linkedTransactions.length) ||
    isDisposition ||
    !!requestNumber;

  const completedOnDate = completedDate || submittedDate;

  // Shown as its own row next to Initiated / Completed rather than replacing
  // either of them, since it is the processor-entered date and time.
  const transactionDate = getActualTransactionDate(actualTransactionDate);

  const { data: allReasons, isLoading } = useAdjustmentReasons(isAdjustment);

  const renderEvent = (
    titleKey: string,
    dateString: string,
    dateFormat: string = "MMM d, yyyy"
  ) => {
    const date = new Date(dateString);

    return (
      <div key={titleKey} className={styles.dateItem}>
        <span>{t(`transactions.details.eventTitles.${titleKey}`)}</span>
        <span className={styles.dateLine}>{format(date, dateFormat)}</span>
        <span className={styles.timeline}>{format(date, "h:mm a")}</span>
      </div>
    );
  };

  const adjustmentReasonsString = useMemo(
    () =>
      isAdjustment
        ? [
          ...adjustmentReasons.map(
            (code) => allReasons?.adjustmentReasonsByCode?.[code].name
          ),
          adjustmentOtherReasonComment,
        ]
          .filter(Boolean)
          .filter((reason) => reason !== adjustmentReasonOtherCode)
          .join(", ")
        : "",
    [
      adjustmentOtherReasonComment,
      adjustmentReasons,
      allReasons?.adjustmentReasonsByCode,
      isAdjustment,
    ]
  );

  return (
    <div className={styles.transactionDataWrapper}>
      <div className={styles.sectionActionContainer}>
        {isLoading && <Loader isBlocking />}
        <div className={styles.transactionTypeWrapper}>
          <h2>{t(`transactions.type.${transactionType}`)}</h2>
          {isLinkedTransactions && (
            <LinkedTransactions
              transactionType={transactionType}
              linkedTransactions={linkedTransactions}
              isStaffView={isStaffView}
              target="_blank"
              targetBusinessRoleCode={
                targetWarehouse?.participant?.businessRoleCode
              }
              requestNumber={requestNumber}
              canOpenRequest={canOpenRequest}
            />
          )}
          {isInitiatedDelivery && (
            <p className={styles.pendingReceiptLabel}>{t("transactions.details.waitingReceipt")}</p>
          )}
        </div>
        <div className={styles.transactionDataContainer}>
          {isRetreader && (
            <div key="Period" className={styles.dateItem}>
              <span>{t(`transactions.details.eventTitles.Period`)}</span>
              <span className={styles.dateLine}>
                {format(utcToZonedTime(new Date((transaction as Retreader).retreadingPeriodDate), "UTC"),
                  "MMM, yyyy"
                )}
              </span>
            </div>
          )}
          {events
            .filter((item) => showedEvents.includes(item.eventType))
            .map((item) => renderEvent(item.eventType, item.createdDate))}
          <div className={styles.transactionDataContainerGroup}>
            {!!completedOnDate &&
              renderEvent(TransactionEventType.Completed, completedOnDate)}
            {!!transactionDate &&
              renderEvent("TransactionDate", transactionDate.toISOString())}
            {isDisposition && regulatoryYear && (
              <div className={styles.dateItem}>
                <span>{t("transactions.details.regYear.title")}</span>
                <span className={styles.dateLine}>{regulatoryYear}</span>
              </div>
            )}
            {isRetreader && submittedBy && (
              <div className={styles.dateItem}>
                <span>{t("transactions.details.createdBy")}</span>
                <span className={styles.dateLine}>
                  {submittedBy.displayName ||
                    t("transactions.details.undefinedUser")}
                </span>
              </div>
            )}
          </div>
          {syncedDate && renderEvent("Synced", syncedDate)}
          {isAdjustment && (
            <span className={styles.adjustmentReasons}>
              {adjustmentReasonsString}
            </span>
          )}
          {shouldShowLocationMap && (
            <LocationsMap
              transaction={transaction as Transaction}
              key={transactionNumber}
              isStaffView={isStaffView}
            />
          )}
        </div>
        <div className={styles.actionsButtonsContainer}>
          <div id={DISPOSITION_EDIT_BUTTON_PORTAL_ID} />
          {sectionAction}
        </div>
      </div>
      {children}
    </div>
  );
};

export default DetailsLocationsSection;
